/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  MultipleQuestionsInput,
  MutationCreateMultipleQuestionArgs,
  MutationCreateQuestionArgs,
  QuestionInput,
  QuestionOutput,
  QuestionQuery,
} from '@/api/gql/graphql';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CREATE_BULK_QUIZZ } from '../mutations';
import { graphqlFetch } from '@/utils/graphqlFetch';
import { GET_QUIZZ } from '../queries/quizz';
import { CREATE_QUIZZ, REMOVE_QUIZZ, UPDATE_QUIZZ } from '../mutations/quizz';

export const useGetQuizz = (query: QuestionQuery) => {
  return useQuery<any[], Error>({
    queryKey: ['quizz'],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getQuestions: any },
        { query: QuestionQuery }
      >(GET_QUIZZ, { query });
      return data.getQuestions.data;
    },
  });
};

export const useCreateBulkQuizz = () => {
  const queryClient = useQueryClient();
  return useMutation<QuestionOutput, Error, MultipleQuestionsInput>({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        {
          createMultipleQuestion: QuestionOutput;
        },
        MutationCreateMultipleQuestionArgs
      >(CREATE_BULK_QUIZZ, { input });
      return data.createMultipleQuestion;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['quizz'],
      }),
  });
};

export const useCreateQuizz = () => {
  const queryClient = useQueryClient();
  return useMutation<QuestionOutput, Error, QuestionInput>({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        {
          createQuestion: QuestionOutput;
        },
        MutationCreateQuestionArgs
      >(CREATE_QUIZZ, { input });
      return data.createQuestion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['quizz'],
      });
      queryClient.invalidateQueries({
        queryKey: ['exercisesWithDisciplinesAndSeries'],
      });
      queryClient.invalidateQueries({
        queryKey: ['exercises'],
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });
};

export const useUpdateQuizz = () => {
  const queryClient = useQueryClient();
  return useMutation<
    QuestionInput,
    Error,
    { input: QuestionInput; updateQuestionId: string }
  >({
    mutationFn: async ({ input, updateQuestionId }) => {
      const data = await graphqlFetch<
        {
          updateQuestion: QuestionInput;
        },
        // Variables du document ($updateQuestionId → arg id du schéma)
        { input: QuestionInput; updateQuestionId: string }
      >(UPDATE_QUIZZ, { updateQuestionId, input });
      return data.updateQuestion;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quizz'] });
      queryClient.invalidateQueries({
        queryKey: ['quizz', variables.updateQuestionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['exercisesWithDisciplinesAndSeries'],
      });
      queryClient.invalidateQueries({
        queryKey: ['exercises'],
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });
};

export const useRemoveQuizz = () => {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, { removeQuestionId: string }>({
    mutationFn: async ({ removeQuestionId }) => {
      await graphqlFetch(REMOVE_QUIZZ, { removeQuestionId });
      return true;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['quizz'],
      }),
  });
};
